import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bell,
  Wifi,
  WifiOff,
  Clock,
  ChevronDown,
  Check,
  AlertTriangle,
  X,
  LogOut,
  User as UserIcon,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { ConnectionStatus } from '../ui/StatusBadge';
import type { WebSocketStatus } from '../../types';
import { currentUser, logout } from '../../lib/auth';

interface HeaderProps {
  wsStatus: WebSocketStatus;
  wsLatency: number;
  className?: string;
}

interface NotificationItem {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
}

function useUKClock() {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return time.toLocaleTimeString('en-GB', {
    timeZone: 'Europe/London',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

function useUKDate() {
  const [date, setDate] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setDate(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  return date.toLocaleDateString('en-GB', {
    timeZone: 'Europe/London',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function Header({ wsStatus, wsLatency, className }: HeaderProps) {
  const [notifications, setNotifications] = useState<NotificationItem[]>([
    {
      id: '1',
      type: 'warning',
      title: 'High Call Volume',
      message: 'Call queue exceeded 15 calls. Consider activating overflow agents.',
      timestamp: new Date(Date.now() - 300000).toISOString(),
      read: false,
    },
    {
      id: '2',
      type: 'success',
      title: 'AI Deflection Milestone',
      message: 'AI handled 85% of calls without human intervention today.',
      timestamp: new Date(Date.now() - 900000).toISOString(),
      read: false,
    },
    {
      id: '3',
      type: 'info',
      title: 'System Update',
      message: 'New agent model v2.4 deployed successfully.',
      timestamp: new Date(Date.now() - 1800000).toISOString(),
      read: true,
    },
  ]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showStatusDetail, setShowStatusDetail] = useState(false);

  const ukTime = useUKClock();
  const ukDate = useUKDate();

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markAsRead = (id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  };

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const formatRelativeTime = (timestamp: string): string => {
    const diff = Date.now() - new Date(timestamp).getTime();
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return `${Math.floor(diff / 86400000)}d ago`;
  };

  const notificationTypeStyles = {
    info: 'bg-info/10 text-info border-info/20',
    success: 'bg-success/10 text-success border-success/20',
    warning: 'bg-warning/10 text-warning border-warning/20',
    error: 'bg-error/10 text-error border-error/20',
  };

  const notificationTypeIcons = {
    info: <Check className="w-3.5 h-3.5" />,
    success: <Check className="w-3.5 h-3.5" />,
    warning: <AlertTriangle className="w-3.5 h-3.5" />,
    error: <X className="w-3.5 h-3.5" />,
  };

  return (
    <header
      className={cn(
        'h-16 flex items-center justify-between px-6',
        'backdrop-blur-xl bg-background/60 border-b border-white/[0.06]',
        className
      )}
    >
      {/* Left: Logo and title */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-vokentra flex items-center justify-center">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="2" fill="none" />
              <circle cx="12" cy="12" r="4" fill="white" />
            </svg>
          </div>
          <div>
            <h1 className="text-sm font-bold text-text-primary">Vokentra Voice Agentic Ai Platform</h1>
            <p className="text-[10px] text-text-muted">Real-time Operations Dashboard</p>
          </div>
        </div>
      </div>

      {/* Right: Status, Clock, Notifications */}
      <div className="flex items-center gap-4">
        {/* WebSocket Status */}
        <div className="relative">
          <motion.button
            onClick={() => setShowStatusDetail(!showStatusDetail)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.03] hover:bg-white/[0.06] transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {wsStatus === 'connected' ? (
              <Wifi className="w-3.5 h-3.5 text-success" />
            ) : (
              <WifiOff className="w-3.5 h-3.5 text-error" />
            )}
            <ConnectionStatus status={wsStatus} />
            {wsLatency > 0 && (
              <span className="text-[10px] text-text-muted font-mono">
                {wsLatency}ms
              </span>
            )}
          </motion.button>

          <AnimatePresence>
            {showStatusDetail && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="absolute right-0 top-full mt-2 w-64 glass-panel p-4 z-50"
              >
                <h3 className="text-xs font-semibold text-text-primary mb-3">
                  Connection Details
                </h3>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-text-muted">Status</span>
                    <span
                      className={cn(
                        'font-medium',
                        wsStatus === 'connected'
                          ? 'text-success'
                          : wsStatus === 'connecting'
                          ? 'text-warning'
                          : 'text-error'
                      )}
                    >
                      {wsStatus}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-muted">Latency</span>
                    <span className="text-text-primary font-mono">{wsLatency}ms</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-muted">Protocol</span>
                    <span className="text-text-primary">WebSocket</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-muted">Endpoint</span>
                    <span className="text-text-primary font-mono text-[10px]">
                      wss://api/v1/stream
                    </span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Clock */}
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.03]">
          <Clock className="w-3.5 h-3.5 text-text-muted" />
          <div className="text-right">
            <motion.p
              className="text-sm font-mono font-semibold text-text-primary tabular-nums"
              key={ukTime}
              initial={{ opacity: 0.7 }}
              animate={{ opacity: 1 }}
            >
              {ukTime}
            </motion.p>
            <p className="text-[9px] text-text-muted">{ukDate} GMT</p>
          </div>
        </div>

        {/* Notification bell */}
        <div className="relative">
          <motion.button
            onClick={() => setShowNotifications(!showNotifications)}
            className={cn(
              'p-2.5 rounded-xl transition-colors relative',
              showNotifications
                ? 'bg-vokentra/20 text-vokentra'
                : 'bg-white/[0.03] text-text-muted hover:text-text-primary hover:bg-white/[0.06]'
            )}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Bell className="w-5 h-5" />

            {/* Badge */}
            {unreadCount > 0 && (
              <motion.span
                className="absolute -top-1 -right-1 w-5 h-5 bg-vokentra text-white text-[10px] font-bold rounded-full flex items-center justify-center"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 500, damping: 25 }}
              >
                {unreadCount}
              </motion.span>
            )}
          </motion.button>

          {/* Notification dropdown */}
          <AnimatePresence>
            {showNotifications && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowNotifications(false)}
                />
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  className="absolute right-0 top-full mt-2 w-80 glass-panel z-50 overflow-hidden"
                >
                  {/* Header */}
                  <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
                    <h3 className="text-sm font-semibold text-text-primary">
                      Notifications
                    </h3>
                    {unreadCount > 0 && (
                      <button
                        onClick={markAllAsRead}
                        className="text-xs text-vokentra hover:text-vokentra-light transition-colors"
                      >
                        Mark all read
                      </button>
                    )}
                  </div>

                  {/* List */}
                  <div className="max-h-[300px] overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="text-center py-8 text-text-muted text-sm">
                        No notifications
                      </div>
                    ) : (
                      notifications.map((notification) => (
                        <motion.div
                          key={notification.id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          className={cn(
                            'p-3 border-b border-white/[0.04] cursor-pointer transition-colors',
                            !notification.read && 'bg-white/[0.02]',
                            notification.read && 'opacity-60'
                          )}
                          onClick={() => markAsRead(notification.id)}
                        >
                          <div className="flex items-start gap-3">
                            <div
                              className={cn(
                                'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 border',
                                notificationTypeStyles[notification.type]
                              )}
                            >
                              {notificationTypeIcons[notification.type]}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-text-primary">
                                {notification.title}
                              </p>
                              <p className="text-[11px] text-text-muted mt-0.5 line-clamp-2">
                                {notification.message}
                              </p>
                              <p className="text-[10px] text-text-muted mt-1">
                                {formatRelativeTime(notification.timestamp)}
                              </p>
                            </div>
                            {!notification.read && (
                              <div className="w-2 h-2 rounded-full bg-vokentra flex-shrink-0 mt-1" />
                            )}
                          </div>
                        </motion.div>
                      ))
                    )}
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>

        {/* User + logout */}
        <div className="flex items-center gap-2 pl-2 ml-1 border-l border-white/[0.08]">
          <div className="hidden sm:flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-white/[0.03]">
            <div className="w-6 h-6 rounded-full bg-vokentra/15 border border-vokentra/30 flex items-center justify-center">
              <UserIcon className="w-3.5 h-3.5 text-vokentra" />
            </div>
            <span className="text-xs text-text-secondary">{currentUser()}</span>
          </div>
          <button
            onClick={() => { logout(); window.location.href = '/'; }}
            title="Sign out"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.03] text-text-muted hover:text-text-primary hover:bg-white/[0.06] transition-colors text-xs"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
      </div>
    </header>
  );
}
